import { useState } from 'react'
import { useQueryClient } from 'react-query'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Check, RotateCcw, Undo2, X } from 'lucide-react'
import type { Match, Transaction } from './types'
import { useCommitMatches } from './hooks/useReconApi'
import { CommitDialog } from './CommitDialog'
import { showSuccessToast, showErrorToast } from '@/hooks/use-toast'

const RECON_BASE_URL =
  import.meta.env.VITE_GO_API_URL || 'http://localhost:8081'

function authHeaders(): HeadersInit {
  const token = localStorage.getItem('access_token')
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  }
}

async function bulkRun<T>(
  items: T[],
  fn: (item: T) => Promise<unknown>,
): Promise<{ ok: number; failed: number }> {
  const results = await Promise.allSettled(items.map(fn))
  const ok = results.filter((r) => r.status === 'fulfilled').length
  return { ok, failed: results.length - ok }
}

interface SelectionBarProps {
  filteredTransactions: Transaction[]
  selectedIds: Set<string>
  clientId: string
  onClearSelection: () => void
}

export function SelectionBar({
  filteredTransactions,
  selectedIds,
  clientId,
  onClearSelection,
}: SelectionBarProps) {
  const queryClient = useQueryClient()
  const commitMutation = useCommitMatches()

  const selectedVisible = filteredTransactions.filter((t) => selectedIds.has(t.id))
  const selectedCount = selectedVisible.length

  const selectedAutoConfirmed = selectedVisible.filter(
    (t) => t.match?.state === 'auto_confirmed' && t.match,
  )
  const selectedConfirmed = selectedVisible.filter(
    (t) => t.match?.state === 'confirmed' && t.match,
  )
  const selectedCommitted = selectedVisible.filter(
    (t) => t.match?.state === 'committed' && t.match,
  )

  const [commitDialogOpen, setCommitDialogOpen] = useState(false)
  const [revertDialogOpen, setRevertDialogOpen] = useState(false)
  const [reverseDialogOpen, setReverseDialogOpen] = useState(false)
  const [bulkHardDelete, setBulkHardDelete] = useState(false)
  const [bulkBusy, setBulkBusy] = useState<null | string>(null)

  const allSelectedCommittedPreExisting =
    selectedCommitted.length > 0 &&
    selectedCommitted.every((t) =>
      t.match!.counterparts.length > 0 &&
      t.match!.counterparts.every((c) => c.mirage_pre_existing === true),
    )

  if (selectedCount === 0) return null

  const invalidateAll = () => {
    queryClient.invalidateQueries(['recon', 'matches'])
    queryClient.invalidateQueries(['recon', 'transactions'])
  }

  const reverseMatches = (matches: Match[], hardDelete: boolean) =>
    bulkRun(matches, (m) =>
      fetch(`${RECON_BASE_URL}/api/v1/reconciliation/matches/${m.id}/reverse`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ client_id: clientId, hard_delete: hardDelete }),
      }).then((r) => {
        if (!r.ok) throw new Error(`${r.status}`)
        return r.json()
      }),
    )

  const handleConfirm = async () => {
    setBulkBusy('confirm')
    const matches = selectedAutoConfirmed.map((t) => t.match!)
    const { ok, failed } = await bulkRun(matches, (m) =>
      fetch(`${RECON_BASE_URL}/api/v1/reconciliation/matches/${m.id}/confirm`, {
        method: 'POST',
        headers: authHeaders(),
      }).then((r) => {
        if (!r.ok) throw new Error(`${r.status}`)
        return r.json()
      }),
    )
    invalidateAll()
    setBulkBusy(null)
    onClearSelection()
    if (failed === 0) showSuccessToast(`Confirmed ${ok} match${ok === 1 ? '' : 'es'}`)
    else showErrorToast(`Confirmed ${ok}, ${failed} failed`)
  }

  const handleUnmatch = async () => {
    setBulkBusy('unmatch')
    const matches = selectedAutoConfirmed.map((t) => t.match!)
    const { ok, failed } = await reverseMatches(matches, true)
    invalidateAll()
    setBulkBusy(null)
    onClearSelection()
    if (failed === 0) showSuccessToast(`Unmatched ${ok} transaction${ok === 1 ? '' : 's'}`)
    else showErrorToast(`Unmatched ${ok}, ${failed} failed`)
  }

  const handleRevert = async () => {
    setBulkBusy('revert')
    const matches = selectedConfirmed.map((t) => t.match!)
    const { ok, failed } = await reverseMatches(matches, true)
    invalidateAll()
    setBulkBusy(null)
    setRevertDialogOpen(false)
    onClearSelection()
    if (failed === 0) showSuccessToast(`Reverted ${ok} match${ok === 1 ? '' : 'es'}`)
    else showErrorToast(`Reverted ${ok}, ${failed} failed`)
  }

  const handleReverse = async () => {
    setBulkBusy('reverse')
    const matches = selectedCommitted.map((t) => t.match!)
    const effectiveHardDelete = allSelectedCommittedPreExisting ? false : bulkHardDelete
    const { ok, failed } = await reverseMatches(matches, effectiveHardDelete)
    invalidateAll()
    setBulkBusy(null)
    setReverseDialogOpen(false)
    setBulkHardDelete(false)
    onClearSelection()
    if (failed === 0) showSuccessToast(`Reversed ${ok} entry${ok === 1 ? '' : ' entries'}`)
    else showErrorToast(`Reversed ${ok}, ${failed} failed`)
  }

  const handleCommitConfirm = (overrides: { doc_nom: string; reg_date: string }) => {
    const ids = selectedConfirmed.map((t) => t.match!.id)
    commitMutation.mutate(
      { match_ids: ids, client_id: clientId, ...overrides },
      {
        onSuccess: () => {
          setCommitDialogOpen(false)
          onClearSelection()
        },
      },
    )
  }

  const breakdown = [
    selectedAutoConfirmed.length && `${selectedAutoConfirmed.length} auto`,
    selectedConfirmed.length && `${selectedConfirmed.length} confirmed`,
    selectedCommitted.length && `${selectedCommitted.length} committed`,
  ]
    .filter(Boolean)
    .join(' · ')

  return (
    <div className="sticky top-0 z-20 -mx-6 border-y border-blue-300 bg-blue-100/95 px-6 py-2.5 shadow-sm backdrop-blur supports-[backdrop-filter]:bg-blue-100/80">
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-sm">
        <div className="flex items-center gap-3">
          <span className="font-semibold text-blue-900">
            {selectedCount} selected
          </span>
          {breakdown && (
            <span className="text-xs text-blue-800/80">{breakdown}</span>
          )}
          <button
            onClick={onClearSelection}
            className="text-xs font-medium text-blue-700 underline-offset-2 hover:underline"
          >
            Clear
          </button>
        </div>

        <div className="ml-auto flex flex-wrap items-center gap-2">
          {selectedAutoConfirmed.length > 0 && (
            <>
              <Button
                size="sm"
                variant="destructive"
                className="gap-1.5"
                disabled={!!bulkBusy}
                onClick={handleUnmatch}
              >
                <X className="h-3.5 w-3.5" />
                {bulkBusy === 'unmatch' ? 'Unmatching...' : `Unmatch ${selectedAutoConfirmed.length}`}
              </Button>
              <Button
                size="sm"
                variant="blue"
                className="gap-1.5"
                disabled={!!bulkBusy}
                onClick={handleConfirm}
              >
                <Check className="h-3.5 w-3.5" />
                {bulkBusy === 'confirm' ? 'Confirming...' : `Confirm ${selectedAutoConfirmed.length}`}
              </Button>
            </>
          )}
          {selectedConfirmed.length > 0 && (
            <>
              <Button
                size="sm"
                variant="destructive"
                className="gap-1.5"
                disabled={!!bulkBusy}
                onClick={() => setRevertDialogOpen(true)}
              >
                <Undo2 className="h-3.5 w-3.5" />
                Revert {selectedConfirmed.length}
              </Button>
              <Button
                size="sm"
                variant="blue"
                className="gap-1.5"
                disabled={commitMutation.isLoading || !!bulkBusy}
                onClick={() => setCommitDialogOpen(true)}
              >
                <Check className="h-3.5 w-3.5" />
                {commitMutation.isLoading
                  ? 'Committing...'
                  : `Commit ${selectedConfirmed.length} selected`}
              </Button>
            </>
          )}
          {selectedCommitted.length > 0 && (
            <Button
              size="sm"
              variant="destructive"
              className="gap-1.5"
              disabled={!!bulkBusy}
              onClick={() => setReverseDialogOpen(true)}
            >
              {allSelectedCommittedPreExisting ? (
                <Undo2 className="h-3.5 w-3.5" />
              ) : (
                <RotateCcw className="h-3.5 w-3.5" />
              )}
              {allSelectedCommittedPreExisting
                ? `Unlink ${selectedCommitted.length}`
                : `Reverse ${selectedCommitted.length}`}
            </Button>
          )}
        </div>
      </div>

      {/* Commit dialog (selection) */}
      <CommitDialog
        open={commitDialogOpen}
        onOpenChange={setCommitDialogOpen}
        transactions={selectedConfirmed}
        clientId={clientId}
        loading={commitMutation.isLoading}
        triggerLabel={`Commit ${selectedConfirmed.length}`}
        onConfirm={handleCommitConfirm}
      />

      {/* Bulk revert (confirmed → unmatched) confirmation */}
      <AlertDialog open={revertDialogOpen} onOpenChange={setRevertDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Revert {selectedConfirmed.length} matches?</AlertDialogTitle>
            <AlertDialogDescription>
              This will remove the selected matches and return their transactions to unmatched. You can re-match them afterwards.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={!!bulkBusy}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-neutral-900 text-white hover:bg-neutral-800"
              disabled={!!bulkBusy}
              onClick={(e) => {
                e.preventDefault()
                handleRevert()
              }}
            >
              {bulkBusy === 'revert' ? 'Reverting...' : 'Revert'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Bulk reverse (committed) confirmation */}
      <AlertDialog
        open={reverseDialogOpen}
        onOpenChange={(open) => {
          setReverseDialogOpen(open)
          if (!open) setBulkHardDelete(false)
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {allSelectedCommittedPreExisting
                ? `Unlink ${selectedCommitted.length} entries?`
                : `Reverse ${selectedCommitted.length} committed entries?`}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {allSelectedCommittedPreExisting
                ? "This removes the links in our system. The journal entries in Mirage stay as-is — you didn't create them through this app."
                : 'This will reverse the journal entries in Mirage ERP with storno records. This action is auditable.'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          {!allSelectedCommittedPreExisting && (
            <label className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50/60 px-3 py-2 text-sm text-amber-900">
              <Checkbox
                checked={bulkHardDelete}
                onCheckedChange={(v) => setBulkHardDelete(v === true)}
                className="mt-0.5"
              />
              <span>
                <span className="font-medium">Hard delete instead of storno</span>
                <span className="block text-xs text-amber-800">
                  Removes the entries entirely instead of leaving an audit-trail storno. Only use when no downstream documents reference them — irreversible.
                </span>
              </span>
            </label>
          )}
          <AlertDialogFooter>
            <AlertDialogCancel disabled={!!bulkBusy}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className={
                allSelectedCommittedPreExisting
                  ? 'bg-neutral-900 text-white hover:bg-neutral-800'
                  : 'bg-red-600 text-white hover:bg-red-700'
              }
              disabled={!!bulkBusy}
              onClick={(e) => {
                e.preventDefault()
                handleReverse()
              }}
            >
              {bulkBusy === 'reverse'
                ? 'Reversing...'
                : allSelectedCommittedPreExisting
                  ? 'Unlink'
                  : bulkHardDelete
                    ? 'Hard delete'
                    : 'Reverse'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
